import { z } from "zod";
import { ArbreMinimalDataSchema, ArbreSchema } from "./arbre.zod";
import { InventaireCreationSourceSchema, metaSchema } from "./commun.zod";
import { EpaysageMinimalDataSchema, EpaysageSchema } from "./epaysage.zod";
import { UserSchema } from "./user.zod";

export { InventaireCreationSourceSchema, type InventaireCreationSource } from "./commun.zod";

export const InventaireBaseSchema = z.object({
  is_finished: z.boolean().default(false).optional(),
  is_deleted: z.boolean().default(false).optional(),
  type: z.string().max(255, "Le type ne peut pas dépasser 255 caractères").nullable().optional(),
  variety_grade: z.number().nullable().optional(),
  health_index: z.number().int().nullable().optional(),
  aesthetic_index: z.number().int().nullable().optional(),
  location_index: z.number().int().nullable().optional(),
  address: z.string().max(100, "L'adresse ne peut pas dépasser 100 caractères").nullable().optional(),
  ville: z.string().max(100, "La ville ne peut pas dépasser 100 caractères").nullable().optional(),
  zip_code: z.string().max(100, "Le code postal ne peut pas dépasser 100 caractères").nullable().optional(),
  aesthetic_column: z.number().int().nullable().optional(),
  health_column: z.number().int().nullable().optional(),
  user_id: z.number().int().positive("L'ID de l'utilisateur doit être un nombre positif"),
  arbre_id: z.number().int().positive("L'ID de l'arbre doit être un nombre positif").nullable().optional(),
  epaysage_id: z.number().int().positive("L'ID de l'espace boisé doit être un nombre positif").nullable().optional(),
  creation_source: InventaireCreationSourceSchema.nullable().optional(),
  arbre: ArbreSchema,
  epaysage: EpaysageSchema,
  user: UserSchema
});

export const InventaireSchema = InventaireBaseSchema.extend({
  id: z.number().int().positive(),
  created_at: z.date(),
  updated_at: z.date().nullable().optional(),
});

export const InventaireCreateSchema = InventaireBaseSchema.pick({
  is_finished: true,
  type: true,
  variety_grade: true,
  health_index: true,
  aesthetic_index: true,
  location_index: true,
  address: true,
  ville: true,
  zip_code: true,
  aesthetic_column: true,
  health_column: true,
  user_id: true,
  arbre_id: true,
  epaysage_id: true,
}).extend({
  client_request_id: z.string().max(128).optional(),
  creation_source: InventaireCreationSourceSchema.optional(),
});

export const InventaireUpdateSchema = z.object({
  id: z.number().int().positive(),
  is_finished: z.boolean().optional(),
  type: z.string().max(255, "Le type ne peut pas dépasser 255 caractères").nullable().optional(),
  variety_grade: z.number().nullable().optional(),
  health_index: z.number().int().nullable().optional(),
  aesthetic_index: z.number().int().nullable().optional(),
  location_index: z.number().int().nullable().optional(),
  address: z.string().max(100, "L'adresse ne peut pas dépasser 100 caractères").nullable().optional(),
  ville: z.string().max(100, "La ville ne peut pas dépasser 100 caractères").nullable().optional(),
  zip_code: z.string().max(100, "Le code postal ne peut pas dépasser 100 caractères").nullable().optional(),
  aesthetic_column: z.number().int().nullable().optional(),
  health_column: z.number().int().nullable().optional(),
  user_id: z.number().int().positive("L'ID de l'utilisateur doit être un nombre positif").optional(),
  arbre_id: z.number().int().positive("L'ID de l'arbre doit être un nombre positif").nullable().optional(),
  epaysage_id: z.number().int().positive("L'ID de l'espace boisé doit être un nombre positif").nullable().optional(),
});

export const InventaireListResponseSchema = z.object({
  data: z.array(InventaireSchema),
  meta: metaSchema
});

export const InventaireResponseSchema = z.object({
  data: InventaireSchema,
  message: z.string().optional(),
  meta: z
    .object({
      mutated_essence_id: z.number().int().positive().optional(),
      idempotent_replay: z.boolean().optional(),
    })
    .optional(),
});

export const InventaireSelectSchema = z.object({
  id: z.number().int().positive().optional(),
  user_id: z.number().int().positive().optional(),
  arbre_id: z.number().int().positive().optional(),
  epaysage_id: z.number().int().positive().optional(),
  type: z.string().optional(),
  is_finished: z.boolean().optional(),
  ville: z.string().optional(),
  address: z.string().optional(),
});

export const InventaireMinimalDataSchema = z.object({
  id: z.number().int().positive(),
  type: z.string().optional(),
  is_finished: z.boolean().optional(),
  ville: z.string().optional(),
  address: z.string().optional(),
  arbre: ArbreMinimalDataSchema,
  epaysage: EpaysageMinimalDataSchema,
});

export type Inventaire = z.infer<typeof InventaireSchema>;
export type InventaireCreateInput = z.infer<typeof InventaireCreateSchema>;
export type InventaireUpdateInput = z.infer<typeof InventaireUpdateSchema>;
export type InventaireSelectInput = z.infer<typeof InventaireSelectSchema>;
export type InventaireMinimalData = z.infer<typeof InventaireMinimalDataSchema>;
// Form-specific types for frontend
export type InventaireFormData = Omit<InventaireCreateInput, 'is_finished'> & {
  is_finished?: boolean;
};

export type InventaireEditFormData = Omit<InventaireUpdateInput, 'id'>;

// Utility types for different UI states
export type InventaireDisplayItem = Pick<Inventaire, 'id' | 'type' | 'address' | 'ville' | 'is_finished'>;
export type InventaireSelectOption = Pick<Inventaire, 'id' | 'type' | 'address'>;
export type InventaireTableItem = Pick<Inventaire, 'id' | 'type' | 'address' | 'ville' | 'is_finished' | 'created_at' | 'updated_at'>;

// For the radius search response
export const GeometryPointSchema = z.object({
  type: z.literal('Point'),
  coordinates: z.tuple([z.number(), z.number()]) // [longitude, latitude]
});

export const InventaireWithGeoSchema = InventaireSchema.extend({
  geometry: GeometryPointSchema.optional(),
  distance: z.number().optional(),
  // Coordinates in {lat, lng} format for easier use with mapping libraries
  coordinates: z.object({
    lat: z.number(),
    lng: z.number()
  }).optional()
});

export const InventaireRadiusResponseSchema = z.object({
  data: z.array(InventaireMinimalDataSchema),
  meta: z.object({
    total: z.number(),
    center: z.object({
      lat: z.number(),
      lng: z.number()
    }),
    radius: z.number()
  })
});

export type InventaireResponse = z.infer<typeof InventaireResponseSchema>;
export type InventaireListResponse = z.infer<typeof InventaireListResponseSchema>;
export type InventaireWithGeo = z.infer<typeof InventaireWithGeoSchema>;
export type InventaireRadiusResponse = z.infer<typeof InventaireRadiusResponseSchema>;

export const validateInventaireCreate = (data: unknown) => {
  return InventaireCreateSchema.safeParse(data);
};

export const validateInventaireUpdate = (data: unknown) => {
  return InventaireUpdateSchema.omit({ id: true }).safeParse(data);
};

export const validateInventaireSelect = (data: unknown) => {
  return InventaireSelectSchema.safeParse(data);
};

// Duplicate inventaire schema
export const InventaireDuplicateSchema = z.object({
  user_id: z.number().int().positive("L'ID de l'utilisateur doit être un nombre positif").optional(),
  type: z.string().max(255, "Le type ne peut pas dépasser 255 caractères").nullable().optional(),
  address: z.string().max(100, "L'adresse ne peut pas dépasser 100 caractères").nullable().optional(),
  ville: z.string().max(100, "La ville ne peut pas dépasser 100 caractères").nullable().optional(),
  copy_arbre_epaysage: z.boolean().default(true).optional(), // Whether to create a new arbre/epaysage or reference existing
  coordinates: z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180)
  }).optional(), // New coordinates for the duplicated inventaire
  polygon: z.array(z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180)
  })).min(3).optional(), // Modified polygon coordinates for epaysage (if provided, will be used instead of calculating offset)
  client_request_id: z.string().max(128).optional(),
  creation_source: InventaireCreationSourceSchema.optional(),
});

export type InventaireDuplicateInput = z.infer<typeof InventaireDuplicateSchema>;

export const validateInventaireDuplicate = (data: unknown) => {
  return InventaireDuplicateSchema.safeParse(data);
};
